import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './DiseaseDropdown.css';
import './DiseaseDropdown-light.css';

// Compact StarRating component
const StarRating = ({ score }) => {
    if (score === null || score === undefined || !Number.isFinite(Number(score))) {
        return <span className="ONCRP-TOOLS-QP-star-rating-na">N/A</span>;
    }
    // Five stars at most: a score outside 0-5 must not make an invalid array length.
    const value = Math.min(Math.max(Number(score), 0), 5);
    const fullStars = Math.floor(value);
    const halfStar = value % 1 >= 0.5 ? 1 : 0;
    const emptyStars = 5 - fullStars - halfStar;
    return (
        <div className="ONCRP-TOOLS-QP-star-rating" title={`Score: ${Number(score).toFixed(2)}`}>
            {[...Array(fullStars)].map((_, i) => <i key={`full-${i}`} className="fas fa-star"></i>)}
            {halfStar === 1 && <i key="half" className="fas fa-star-half-alt"></i>}
            {[...Array(emptyStars)].map((_, i) => <i key={`empty-${i}`} className="far fa-star"></i>)}
        </div>
    );
};

const DiseaseDropdown = ({ diseases = [], selectedDiseaseName, onSelect }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [evidenceModal, setEvidenceModal] = useState(null);
    const [isLightTheme, setIsLightTheme] = useState(false);
    const dropdownRef = useRef(null);

    const handleSelect = (disease) => {
        onSelect(disease);
        setIsOpen(false);
    };

    const handleEvidenceClick = (e, disease) => {
        e.stopPropagation();
        setEvidenceModal(disease);
    };

    const closeEvidenceModal = () => {
        setEvidenceModal(null);
    };

    // Theme detection effect
    useEffect(() => {
        const checkTheme = () => {
            if (typeof document !== 'undefined') {
                const hasLightClass = document.documentElement.classList.contains('ocrp-theme-light');
                setIsLightTheme(hasLightClass);
            }
        };

        checkTheme();

        if (typeof window !== 'undefined') {
            const handleThemeChange = (event) => {
                if (event.detail && typeof event.detail.theme === 'string') {
                    setIsLightTheme(event.detail.theme === 'light');
                }
            };

            window.addEventListener('ocrp-theme-change', handleThemeChange);
            return () => window.removeEventListener('ocrp-theme-change', handleThemeChange);
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [dropdownRef]);

    const selectedDiseaseObject = diseases.find(d => d.name === selectedDiseaseName);

    return (
        <>
            <div className="ONCRP-TOOLS-QP-dropdown-container">
                <div className="ONCRP-TOOLS-QP-custom-dropdown" ref={dropdownRef}>
                    <div className="ONCRP-TOOLS-QP-dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
                        <div className="ONCRP-TOOLS-QP-trigger-content">
                            <i className="fas fa-project-diagram ONCRP-TOOLS-QP-trigger-icon"></i>
                            <span className="ONCRP-TOOLS-QP-trigger-text">
                                {selectedDiseaseObject?.name || 'Select Disease'}
                            </span>
                        </div>
                        <div className="ONCRP-TOOLS-QP-trigger-meta">
                            {selectedDiseaseObject && <StarRating score={selectedDiseaseObject.evidence_score} />}
                            <i className={`fas fa-chevron-down ONCRP-TOOLS-QP-chevron ${isOpen ? 'open' : ''}`}></i>
                        </div>
                    </div>

                    {isOpen && (
                        <div className="ONCRP-TOOLS-QP-dropdown-list">
                            {diseases.map((disease) => (
                                <div
                                    key={disease.name}
                                    className={`ONCRP-TOOLS-QP-dropdown-item ${disease.name === selectedDiseaseName ? 'selected' : ''}`}
                                    onClick={() => handleSelect(disease)}
                                >
                                    <div className="ONCRP-TOOLS-QP-item-header">
                                        <span className="ONCRP-TOOLS-QP-item-name">{disease.name}</span>
                                        <StarRating score={disease.evidence_score} />
                                    </div>
                                    <div className="ONCRP-TOOLS-QP-item-footer">
                                        <span className="ONCRP-TOOLS-QP-item-sources">{disease.sources?.join(', ') || 'No sources'}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Evidence Button - only show for selected disease with evidence */}
                {selectedDiseaseObject && selectedDiseaseObject.civic_evidence_items?.length > 0 && (
                    <button 
                        className="ONCRP-TOOLS-QP-external-evidence-button" 
                        onClick={() => setEvidenceModal(selectedDiseaseObject)}
                        title={`View ${selectedDiseaseObject.civic_evidence_items.length} Evidence Items for ${selectedDiseaseObject.name}`}
                    >
                        <i className="fas fa-file-alt"></i>
                        <span>{selectedDiseaseObject.civic_evidence_items.length}</span>
                    </button>
                )}
            </div>

            {/* Evidence Modal - Using React Portal */}
            {evidenceModal && createPortal(
                <div className="ONCRP-TOOLS-QP-evidence-modal-overlay" onClick={closeEvidenceModal}>
                    <div className="ONCRP-TOOLS-QP-evidence-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ONCRP-TOOLS-QP-modal-header">
                            <div className="ONCRP-TOOLS-QP-modal-title">
                                <i className="fas fa-file-alt"></i>
                                <span>Evidence Items for {evidenceModal.name}</span>
                            </div>
                            <button className="ONCRP-TOOLS-QP-modal-close" onClick={closeEvidenceModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="ONCRP-TOOLS-QP-modal-body">
                            {evidenceModal.civic_evidence_items?.map((item, index) => (
                                <div key={index} className="ONCRP-TOOLS-QP-evidence-item">
                                    <div className="ONCRP-TOOLS-QP-evidence-top-row">
                                        <div className="ONCRP-TOOLS-QP-gene-variant">
                                            <span className="ONCRP-TOOLS-QP-gene-badge">{item.gene}</span>
                                            <span className="ONCRP-TOOLS-QP-variant-badge">{item.variant}</span>
                                        </div>
                                        <div className="ONCRP-TOOLS-QP-evidence-scores">
                                            <span className="ONCRP-TOOLS-QP-level-badge">Level {item.evidence_level || 'N/A'}</span>
                                            <span className="ONCRP-TOOLS-QP-rating-badge">Rating: {item.evidence_rating || 'N/A'}/5</span>
                                        </div>
                                    </div>
                                    
                                    <div className="ONCRP-TOOLS-QP-evidence-type-row">
                                        <span className={`ONCRP-TOOLS-QP-type-badge ${item.evidence_type?.toLowerCase().replace('sensitivityresponse', 'sensitivity')}`}>
                                            {item.evidence_type?.replace('SENSITIVITYRESPONSE', 'SENSITIVITY/RESPONSE') || 'Unknown'}
                                        </span>
                                        <span className={`ONCRP-TOOLS-QP-direction-badge ${item.evidence_direction?.toLowerCase().replace('_', '-')}`}>
                                            <i className={`fas ${item.evidence_direction === 'SUPPORTS' ? 'fa-check' : 
                                                item.evidence_direction === 'DOES_NOT_SUPPORT' ? 'fa-times' : 'fa-question'}`}></i>
                                            {item.evidence_direction?.replace('_', ' ') || 'Unknown'}
                                        </span>
                                    </div>
                                    
                                    <div className="ONCRP-TOOLS-QP-drugs-section">
                                        <span className="ONCRP-TOOLS-QP-drugs-label">
                                            <i className="fas fa-pills"></i>
                                            Drugs:
                                        </span>
                                        <div className="ONCRP-TOOLS-QP-drugs-container">
                                            {item.drugs?.map((drug, drugIndex) => (
                                                <span key={drugIndex} className="ONCRP-TOOLS-QP-drug-pill">
                                                    {drug}
                                                </span>
                                            )) || <span className="ONCRP-TOOLS-QP-no-drugs">No drugs specified</span>}
                                        </div>
                                    </div>
                                    
                                    <div className="ONCRP-TOOLS-QP-evidence-footer">
                                        <span className="ONCRP-TOOLS-QP-disease-tag">
                                            <i className="fas fa-disease"></i>
                                            {item.disease}
                                        </span>
                                        {item.pubmed_id && (
                                            <a 
                                                href={`https://pubmed.ncbi.nlm.nih.gov/${item.pubmed_id}/`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="ONCRP-TOOLS-QP-pubmed-link"
                                            >
                                                <i className="fas fa-external-link-alt"></i>
                                                PubMed: {item.pubmed_id}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default DiseaseDropdown;